import { FooterSection } from "@/components/landing/FooterSection";
import { CheckCircle } from "lucide-react";

export default function Pricing() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto px-6 py-16 md:py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            Simple, Transparent Pricing
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Choose Your <span className="text-orange-500">Perfect Plan</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>
        </div>
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Startup & Founders Plan */}
          <div className="bg-card border border-border rounded-2xl p-8 relative">
            <div className="absolute -top-3 left-8">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium border border-blue-200">
                Early Stage
              </span>
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-foreground mb-2">Startup & Founders</h3>
              <div className="text-4xl font-bold text-foreground mb-2">
                ₹7,999<span className="text-lg font-normal text-muted-foreground">/month</span>
              </div>
              <p className="text-muted-foreground">Perfect for early-stage startups and individual founders</p>
            </div>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>5 active tests</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>20 minutes audio/video per test</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>AI-powered evaluations</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Basic proctoring features</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Email support</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Standard question bank</span>
              </li>
            </ul>
          </div>

          {/* Recruiter Plan */}
          <div className="bg-card border-2 border-orange-500 rounded-2xl p-8 relative shadow-lg">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                Most Popular
              </span>
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-foreground mb-2 flex items-center gap-2">
                Recruiter
                <span className="text-gray-400 text-sm">HR Professionals ⚡</span>
              </h3>
              <div className="text-4xl font-bold text-foreground mb-2">
                ₹14,999<span className="text-lg font-normal text-muted-foreground">/month</span>
              </div>
              <p className="text-muted-foreground">Ideal for HR professionals and recruitment agencies</p>
            </div>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>25 active tests</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>60 minutes audio/video per test</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>AI-powered evaluations</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Advanced proctoring</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Priority email support</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Expert verified question bank</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Skill-based analysis reports</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Bulk candidate processing</span>
              </li>
            </ul>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-card border border-border rounded-2xl p-8 relative">
            <div className="absolute -top-3 left-8">
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium border border-purple-200">
                Large Organizations
              </span>
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise</h3>
              <div className="text-4xl font-bold text-foreground mb-2">Custom</div>
              <p className="text-muted-foreground">Comprehensive solution for large organizations</p>
            </div>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Unlimited active tests</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Unlimited AI evaluations</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Skill based Analysis reports</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Advanced Proctoring</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Priority Email support</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Priority Phone support</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Expert verified question bank</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Enterprise-grade security</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Dedicated account manager</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Custom integrations</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>SLA guarantees</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500" />
                <span>Bulk candidate processing</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Features */}
        <div className="flex flex-wrap justify-center gap-8 mb-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Save 16% on yearly plans
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-blue-500" />
            No setup fees
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-purple-500" />
            Expert support included
          </div>
        </div>

        {/* Bottom Text */}
        <div className="text-center text-muted-foreground max-w-4xl mx-auto">
          <p className="mb-2">All plans include our core AI assessment features. Contact sales for custom requirements.</p>
          <p className="font-medium">Enterprise pricing is customized based on your needs.</p>
        </div>
      </div>
      <FooterSection />
    </div>
  );
}